import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// ── Transforms ──────────────────────────────────────────

function transformCompany(r) {
  return {
    id: r.id,
    branchId: r.branch_id,
    companyName: r.company_name,
    contactPhone: r.contact_phone,
    contactEmail: r.contact_email,
    active: r.active,
    createdAt: r.created_at,
  }
}

function transformDriver(r) {
  return {
    id: r.id,
    branchId: r.branch_id,
    fullName: r.full_name,
    licencePlate: r.licence_plate,
    phone: r.phone,
    active: r.active,
    createdAt: r.created_at,
  }
}

function transformSale(r) {
  return {
    id: r.id,
    branchId: r.branch_id,
    transportCompanyId: r.transport_company_id,
    driverId: r.driver_id,
    receiptNumber: r.receipt_number,
    saleTime: r.sale_time,
    productType: r.product_type,
    litres: Number(r.litres),
    unitPriceAtSale: Number(r.unit_price_at_sale),
    totalValue: Number(r.total_value),
    destination: r.destination,
    status: r.status,
    weeklyBatchId: r.week_batch_id,
    notes: r.notes,
    editedAt: r.edited_at,
    editReason: r.edit_reason,
    // joined fields
    companyName: r.transport_companies?.company_name ?? '',
    driverName: r.drivers?.full_name ?? '',
    licencePlate: r.drivers?.licence_plate ?? '',
  }
}

function transformBatch(r) {
  return {
    id: r.id,
    branchId: r.branch_id,
    weekStart: r.week_start,
    weekEnd: r.week_end,
    status: r.status,
    totalLitres: Number(r.total_litres),
    totalValue: Number(r.total_value),
    exportedAt: r.exported_at,
    reconciledAt: r.reconciled_at,
    createdAt: r.created_at,
  }
}

function buildPriceMap(rows) {
  // rows ordered effective_from DESC — first match per branch+product wins.
  // A price dated in the future is scheduled, not current: it must not start
  // pricing sales before the moment it takes effect.
  const now = Date.now()
  const map = {}
  rows.filter((r) => new Date(r.effective_from).getTime() <= now).forEach((r) => {
    if (!map[r.branch_id]) map[r.branch_id] = {}
    if (!map[r.branch_id][r.product_type]) {
      map[r.branch_id][r.product_type] = {
        id: r.id,
        unitPrice: Number(r.unit_price),
        effectiveFrom: r.effective_from,
        setBy: r.set_by,
      }
    }
  })
  return map
}

function buildPriceHistory(rows) {
  const map = {}
  rows.forEach((r) => {
    if (!map[r.branch_id]) map[r.branch_id] = {}
    if (!map[r.branch_id][r.product_type]) map[r.branch_id][r.product_type] = []
    map[r.branch_id][r.product_type].push({
      id: r.id,
      unitPrice: Number(r.unit_price),
      effectiveFrom: r.effective_from,
      setBy: r.set_by,
    })
  })
  return map
}

// ── Hook ────────────────────────────────────────────────

export function useCreditModule(currentUser) {
  const [companies, setCompanies] = useState([])
  const [drivers, setDrivers] = useState([])
  const [prices, setPrices] = useState({})          // { [branchId]: { AGO: {...}, PMS: {...}, DPK: {...} } }
  const [priceHistory, setPriceHistory] = useState({}) // same shape, all rows
  const [sales, setSales] = useState([])
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!currentUser) return
    fetchAll()
  }, [currentUser?.id])

  async function fetchAll() {
    setLoading(true)
    const [coRes, drRes, saRes, baRes, prRes] = await Promise.all([
      supabase.from('transport_companies').select('*').order('company_name'),
      supabase.from('drivers').select('*').eq('active', true).order('full_name'),
      supabase
        .from('credit_sales')
        .select('*, transport_companies(company_name), drivers(full_name, licence_plate)')
        .order('sale_time', { ascending: false })
        .limit(1000),
      supabase.from('weekly_batches').select('*').order('week_start', { ascending: false }),
      supabase.from('product_price_history').select('*').order('effective_from', { ascending: false }),
    ])
    setCompanies((coRes.data || []).map(transformCompany))
    setDrivers((drRes.data || []).map(transformDriver))
    setSales((saRes.data || []).map(transformSale))
    setBatches((baRes.data || []).map(transformBatch))
    setPrices(buildPriceMap(prRes.data || []))
    setPriceHistory(buildPriceHistory(prRes.data || []))
    setLoading(false)
  }

  // ── Credit Sales ───────────────────────────────────────

  async function recordSale({ branchId, transportCompanyId, driverId, productType, litres, destination, notes }) {
    const price = prices[branchId]?.[productType]
    if (!price) {
      return { success: false, message: `No price set for ${productType} on this branch. Set a price first.` }
    }

    const { data: receiptNum, error: rpcErr } = await supabase.rpc('next_receipt_number', { p_branch_id: branchId })
    if (rpcErr) return { success: false, message: rpcErr.message }

    const { data: { session } } = await supabase.auth.getSession()
    const totalValue = Number((litres * price.unitPrice).toFixed(2))

    const { data, error } = await supabase
      .from('credit_sales')
      .insert({
        branch_id: branchId,
        transport_company_id: transportCompanyId,
        driver_id: driverId,
        price_snapshot_id: price.id,
        recorded_by: session?.user?.id,
        receipt_number: receiptNum,
        product_type: productType,
        litres: Number(litres),
        unit_price_at_sale: price.unitPrice,
        total_value: totalValue,
        destination,
        notes: notes || '',
      })
      .select('*, transport_companies(company_name), drivers(full_name, licence_plate)')
      .single()

    if (error) return { success: false, message: error.message }
    const newSale = transformSale(data)
    setSales((prev) => [newSale, ...prev])
    return { success: true, sale: newSale }
  }

  async function editSale(saleId, updates, reason) {
    const { error } = await supabase
      .from('credit_sales')
      .update({
        litres: updates.litres,
        destination: updates.destination,
        notes: updates.notes,
        total_value: Number((updates.litres * updates.unitPriceAtSale).toFixed(2)),
        edited_at: new Date().toISOString(),
        edit_reason: reason || '',
      })
      .eq('id', saleId)
    if (error) return { success: false, message: error.message }
    await fetchAll()
    return { success: true }
  }

  // ── Prices ─────────────────────────────────────────────

  // `effectiveFrom` is when the station actually changed the price, which is
  // not always when someone got round to entering it. Backdating corrects the
  // history; it does NOT re-price sales already recorded, because each sale
  // snapshots unit_price_at_sale when it is written.
  async function updatePrice(branchId, productType, unitPrice, effectiveFrom) {
    const { data: { session } } = await supabase.auth.getSession()
    const { error } = await supabase.from('product_price_history').insert({
      branch_id: branchId,
      product_type: productType,
      unit_price: Number(unitPrice),
      effective_from: effectiveFrom ? new Date(effectiveFrom).toISOString() : new Date().toISOString(),
      set_by: session?.user?.id,
    })
    if (error) return { success: false, message: error.message }
    await fetchAll()
    return { success: true }
  }

  // ── Transport Companies ────────────────────────────────

  async function addCompany({ branchId, companyName, contactPhone, contactEmail }) {
    const { data, error } = await supabase
      .from('transport_companies')
      .insert({ branch_id: branchId, company_name: companyName, contact_phone: contactPhone || '', contact_email: contactEmail || '' })
      .select()
      .single()
    if (error) return { success: false, message: error.message }
    setCompanies((prev) => [...prev, transformCompany(data)].sort((a, b) => a.companyName.localeCompare(b.companyName)))
    return { success: true, id: data.id }
  }

  async function updateCompany(id, updates) {
    const { error } = await supabase
      .from('transport_companies')
      .update({ company_name: updates.companyName, contact_phone: updates.contactPhone || '', contact_email: updates.contactEmail || '' })
      .eq('id', id)
    if (error) return { success: false, message: error.message }
    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)))
    return { success: true }
  }

  async function toggleCompany(id, active) {
    await supabase.from('transport_companies').update({ active }).eq('id', id)
    setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, active } : c)))
  }

  // ── Drivers ────────────────────────────────────────────

  async function addDriver({ branchId, fullName, licencePlate, phone }) {
    const { data, error } = await supabase
      .from('drivers')
      .insert({ branch_id: branchId, full_name: fullName, licence_plate: licencePlate || '', phone: phone || '' })
      .select()
      .single()
    if (error) return { success: false, message: error.message }
    setDrivers((prev) => [...prev, transformDriver(data)].sort((a, b) => a.fullName.localeCompare(b.fullName)))
    return { success: true, id: data.id }
  }

  async function updateDriver(id, updates) {
    const { error } = await supabase
      .from('drivers')
      .update({ full_name: updates.fullName, licence_plate: updates.licencePlate || '', phone: updates.phone || '' })
      .eq('id', id)
    if (error) return { success: false, message: error.message }
    setDrivers((prev) => prev.map((d) => (d.id === id ? { ...d, ...updates } : d)))
    return { success: true }
  }

  async function toggleDriver(id, active) {
    await supabase.from('drivers').update({ active }).eq('id', id)
    setDrivers((prev) => prev.map((d) => (d.id === id ? { ...d, active } : d)))
  }

  // ── Batches ────────────────────────────────────────────

  async function exportBatch(batchId) {
    const { data: { session } } = await supabase.auth.getSession()
    const { error } = await supabase
      .from('weekly_batches')
      .update({ status: 'exported', exported_at: new Date().toISOString(), exported_by: session?.user?.id })
      .eq('id', batchId)
    if (error) return { success: false, message: error.message }
    await supabase.from('credit_sales').update({ status: 'exported' }).eq('week_batch_id', batchId)
    await fetchAll()
    return { success: true }
  }

  async function reconcileBatch(batchId) {
    const { error } = await supabase.from('weekly_batches').update({ status: 'reconciled', reconciled_at: new Date().toISOString() }).eq('id', batchId)
    if (error) return { success: false, message: error.message }
    await supabase.from('credit_sales').update({ status: 'reconciled' }).eq('week_batch_id', batchId)
    await fetchAll()
    return { success: true }
  }

  // ── Bulk Import ───────────────────────────────────────

  async function bulkImportSales(validRows, branchId, onProgress) {
    const { data: { session } } = await supabase.auth.getSession()
    const createdDrivers = {} // fullName.toLowerCase() → driver id (within this import)
    let successCount = 0
    const errors = []

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i]
      try {
        let driverId = row._driver?.id
        if (!driverId) {
          const key = row.driver_name.toLowerCase()
          if (createdDrivers[key]) {
            driverId = createdDrivers[key]
          } else {
            const { data: driverData, error: driverErr } = await supabase
              .from('drivers')
              .insert({ branch_id: branchId, full_name: row.driver_name.trim(), licence_plate: row.licence_plate?.trim() || '', phone: '' })
              .select()
              .single()
            if (driverErr) throw new Error(`Row ${row._rowNum}: Failed to create driver — ${driverErr.message}`)
            createdDrivers[key] = driverData.id
            driverId = driverData.id
            setDrivers((prev) => [...prev, transformDriver(driverData)].sort((a, b) => a.fullName.localeCompare(b.fullName)))
          }
        }

        const { data: receiptNum, error: rpcErr } = await supabase.rpc('next_receipt_number', { p_branch_id: branchId })
        if (rpcErr) throw new Error(`Row ${row._rowNum}: ${rpcErr.message}`)

        const unitPriceAtSale = row.unitPrice
        const totalValue = Number((row.litres * unitPriceAtSale).toFixed(2))
        const saleTime = new Date(row.sale_date + 'T12:00:00').toISOString()
        // Use stored snapshot id for reference if available; nullable in DB
        const priceSnapshotId = prices[branchId]?.[row.product]?.id ?? null

        const { data: saleData, error: saleErr } = await supabase
          .from('credit_sales')
          .insert({
            branch_id: branchId,
            transport_company_id: row._company.id,
            driver_id: driverId,
            price_snapshot_id: priceSnapshotId,
            recorded_by: session?.user?.id,
            receipt_number: receiptNum,
            product_type: row.product,
            litres: row.litres,
            unit_price_at_sale: unitPriceAtSale,
            total_value: totalValue,
            destination: row.destination.trim(),
            notes: row.notes?.trim() || '',
            sale_time: saleTime,
          })
          .select('*, transport_companies(company_name), drivers(full_name, licence_plate)')
          .single()

        if (saleErr) throw new Error(`Row ${row._rowNum}: ${saleErr.message}`)
        setSales((prev) => [transformSale(saleData), ...prev])
        successCount++
      } catch (err) {
        errors.push(err.message)
      }
      onProgress(i + 1)
    }

    await fetchAll()
    return { success: successCount, failed: errors.length, errors }
  }

  // ── Helpers ────────────────────────────────────────────

  function getPastDestinations(branchId) {
    const seen = new Set()
    return sales
      .filter((s) => s.branchId === branchId && s.destination)
      .map((s) => s.destination)
      .filter((d) => { if (seen.has(d)) return false; seen.add(d); return true })
  }

  function getCurrentPrice(branchId, productType) {
    return prices[branchId]?.[productType] ?? null
  }

  function getSalesForBranch(branchId) {
    return sales.filter((s) => s.branchId === branchId)
  }

  function getTodaySalesSummary(branchId) {
    const todayStr = new Date().toISOString().slice(0, 10)
    const todaySales = sales.filter((s) => s.branchId === branchId && s.saleTime.slice(0, 10) === todayStr)
    return {
      count: todaySales.length,
      litres: todaySales.reduce((s, x) => s + x.litres, 0),
      value: todaySales.reduce((s, x) => s + x.totalValue, 0),
    }
  }

  function getTodaySalesSummaryForOrg() {
    const todayStr = new Date().toISOString().slice(0, 10)
    const todaySales = sales.filter((s) => s.saleTime.slice(0, 10) === todayStr)
    return {
      count: todaySales.length,
      litres: todaySales.reduce((s, x) => s + x.litres, 0),
      value: todaySales.reduce((s, x) => s + x.totalValue, 0),
    }
  }

  return {
    companies, drivers, prices, priceHistory, sales, batches, loading,
    recordSale, editSale,
    updatePrice,
    addCompany, updateCompany, toggleCompany,
    addDriver, updateDriver, toggleDriver,
    exportBatch, reconcileBatch,
    getPastDestinations, getCurrentPrice, getSalesForBranch, getTodaySalesSummary, getTodaySalesSummaryForOrg,
    bulkImportSales,
    refresh: fetchAll,
  }
}
